import type { IPlansRepository, Plan, PlanCustomerType, PlanKind, ProposalPlanLine, UpsertPlanInput } from '../ports/IPlansRepository';

const PLAN_KINDS: PlanKind[] = ['plan', 'addon'];
const CUSTOMER_TYPES: PlanCustomerType[] = ['varejo', 'corporate', 'ambos'];

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export class PlansService {
  constructor(private readonly repo: IPlansRepository) {}

  listPlans = (workspaceId: string): Promise<Plan[]> => {
    if (!workspaceId) return Promise.resolve([]);
    return this.repo.listPlans(workspaceId);
  };

  listAllPlans = (workspaceId: string): Promise<Plan[]> => {
    if (!workspaceId) return Promise.resolve([]);
    return this.repo.listAllPlans(workspaceId);
  };

  savePlan = (input: UpsertPlanInput): Promise<Plan> => {
    const name = String(input.name || '').trim();
    if (!input.workspaceId) throw new Error('Workspace obrigatorio.');
    if (!name) throw new Error('Nome do plano obrigatorio.');
    if (!Number.isFinite(Number(input.unitValue)) || Number(input.unitValue) < 0) {
      throw new Error('Valor do plano invalido.');
    }
    const kind = input.kind ?? 'plan';
    const customerType = input.customerType ?? 'corporate';
    if (!PLAN_KINDS.includes(kind)) throw new Error('Tipo do plano invalido.');
    if (!CUSTOMER_TYPES.includes(customerType)) throw new Error('Tabela de preco invalida.');
    return this.repo.savePlan({
      ...input,
      name,
      description: input.description ? String(input.description).trim() : null,
      unitValue: Number(input.unitValue),
      orderIndex: Math.max(0, Number(input.orderIndex || 0)),
      kind,
      customerType,
    });
  };

  setPlanActive = (planId: string, ativo: boolean): Promise<void> => {
    if (!planId) return Promise.resolve();
    return this.repo.setPlanActive(planId, ativo);
  };

  normalizeProposalPlanLines = (lines: unknown): ProposalPlanLine[] => (
    Array.isArray(lines) ? lines : []
  )
    .filter((line): line is Record<string, unknown> => !!line && typeof line === 'object')
    .map((line) => {
      const quantity = Math.max(0, toNumber(line.quantity));
      const unitValue = Math.max(0, toNumber(line.unitValue));
      // `catalogValue` só existe quando alguém guardou o preço de tabela. Zero
      // é valor legítimo (plano gratuito), então o teste é de presença, não de
      // verdade — `|| undefined` apagaria a tabela de um plano de R$ 0.
      const hasCatalog = line.catalogValue != null && Number.isFinite(Number(line.catalogValue));
      const months = Math.floor(toNumber(line.priceMonths));
      return {
        planId: String(line.planId || ''),
        name: String(line.name || ''),
        quantity,
        unitValue,
        totalValue: Math.round(quantity * unitValue * 100) / 100,
        // Proposta anterior à v2 não tem `kind`. Assumir 'plan' preserva o
        // comportamento antigo: tudo que estava lá contava como vida.
        kind: line.kind === 'addon' ? 'addon' as const : 'plan' as const,
        ...(hasCatalog ? { catalogValue: Math.max(0, Number(line.catalogValue)) } : {}),
        ...(months > 0 ? { priceMonths: months } : {}),
      };
    })
    .filter(line => !!line.planId && !!line.name && line.quantity > 0);

  // ── Preço negociado ─────────────────────────────────────────────
  // Quem manda no valor da venda, do contrato e do boleto é sempre o
  // `unitValue` da linha. `catalogValue` é memória: serve para a tela mostrar
  // de quanto era e para desfazer a personalização sem perguntar ao catálogo
  // de novo — o plano pode ter mudado de preço, ou saído do ar, desde então.

  /** O preço de tabela lembrado na linha. Sem ele, o próprio `unitValue` é a
   *  tabela: é o caso de toda linha criada antes desta mudança. */
  catalogUnitValue = (line: ProposalPlanLine | null | undefined): number => (
    line?.catalogValue != null && Number.isFinite(Number(line.catalogValue))
      ? Math.max(0, Number(line.catalogValue))
      : Math.max(0, toNumber(line?.unitValue))
  );

  /** A linha saiu da tabela? Comparação com folga de meio centavo: o valor
   *  passa por `toLocaleString` e volta por `parseMoney` a cada edição. */
  isCustomPricedLine = (line: ProposalPlanLine | null | undefined): boolean =>
    Math.abs(toNumber(line?.unitValue) - this.catalogUnitValue(line)) >= 0.005;

  hasCustomPricedLines = (lines: unknown): boolean =>
    this.normalizeProposalPlanLines(lines).some(this.isCustomPricedLine);

  /** Desliga a personalização: cada linha volta ao preço de tabela lembrado e
   *  perde o prazo da condição. Linha sem `catalogValue` fica como está —
   *  inventar uma tabela que ninguém guardou seria pior do que manter o que
   *  foi negociado. */
  restoreCatalogPrices = (lines: unknown): ProposalPlanLine[] =>
    this.normalizeProposalPlanLines(lines).map((line) => {
      const catalog = this.catalogUnitValue(line);
      const { priceMonths, ...semPrazo } = line;
      return {
        ...semPrazo,
        unitValue: catalog,
        totalValue: Math.round(line.quantity * catalog * 100) / 100,
      };
    });

  calculateProposalPlansTotal = (lines: unknown): number =>
    this.normalizeProposalPlanLines(lines)
      .reduce((sum, line) => sum + line.totalValue, 0);

  // VIDAS = só as linhas de plano. Adicional entra no valor, nunca na contagem
  // de vidas — é o que separa faturamento de meta no Enterprise.
  countProposalLives = (lines: unknown): number =>
    this.normalizeProposalPlanLines(lines)
      .filter(line => line.kind !== 'addon')
      .reduce((sum, line) => sum + Math.max(0, Math.floor(line.quantity)), 0);

  // Regra de negócio: cada adicional só pode ser contratado em quantidade igual
  // ou inferior ao total de vidas. Devolve as linhas que estouram, para a tela
  // dizer qual é o problema em vez de só desabilitar o botão. A RPC aplica a
  // mesma regra no banco.
  findAddonsOverLimit = (lines: unknown): ProposalPlanLine[] => {
    const normalized = this.normalizeProposalPlanLines(lines);
    const lives = this.countProposalLives(normalized);
    return normalized.filter(line => line.kind === 'addon' && line.quantity > lives);
  };
}
